import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, BadgeCheck, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import CountrySelect from '@/components/common/CountrySelect';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ConfirmEmailStep from '@/components/onboarding/ConfirmEmailStep';
import { ORGS_QUERY_KEY } from '@/hooks/useOrgsQuery';
import { switchOrgAction } from '@/components/organizations/actions';
import { fetchEmailVerification } from '@/data/billing';
import {
  createAdvisorOrganization,
  createAdvisorProfile,
} from '@/data/advisor-certification';

const STEPS = ['Confirm email', 'Your practice', 'Your profile', 'You are in'];


function Steps({ current }: { current: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs" data-testid="advisor-onboarding-steps">
      {STEPS.map((label, index) => (
        <li key={label} className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${
              index < current
                ? 'border-amber-500 bg-amber-500 text-white'
                : index === current
                  ? 'border-amber-500 text-amber-600'
                  : 'border-muted text-muted-foreground'
            }`}
          >
            {index < current ? <Check className="h-3 w-3" /> : index + 1}
          </span>
          <span className={index === current ? 'font-medium' : 'text-muted-foreground'}>
            {label}
          </span>
          {index < STEPS.length - 1 && <span className="text-muted-foreground">·</span>}
        </li>
      ))}
    </ol>
  );
}

export default function AdvisorOnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [firmName, setFirmName] = React.useState('');
  const [orgId, setOrgId] = React.useState<string | null>(null);
  const [displayName, setDisplayName] = React.useState('');
  const [headline, setHeadline] = React.useState('');
  const [bio, setBio] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [languages, setLanguages] = React.useState('');

  React.useEffect(() => {
    fetchEmailVerification()
      .then((verification) => setStep(verification.verified ? 1 : 0))
      .catch(() => setStep(0));
  }, []);

  const createPractice = async () => {
    if (!firmName.trim()) return;
    setBusy(true);
    try {
      const org = await createAdvisorOrganization(firmName.trim());
      setOrgId(org.id);
      await switchOrgAction({ orgId: org.id });
      await queryClient.invalidateQueries({ queryKey: ORGS_QUERY_KEY });
      setStep(2);
    } catch (error) {
      toast.error(apiError(error, 'Could not create your organisation.'));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    if (!displayName.trim()) return;
    setBusy(true);
    try {
      await createAdvisorProfile(
        {
          display_name: displayName.trim(),
          headline: headline.trim(),
          bio: bio.trim(),
          country: country.trim(),
          languages: languages.trim(),
        },
        orgId ?? undefined,
      );
      setStep(3);
    } catch (error) {
      toast.error(apiError(error, 'Could not save your profile.'));
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="min-h-screen w-full bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-3xl space-y-6" data-testid="advisor-onboarding-page">
        <Steps current={step} />

        {step === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Confirm your email</CardTitle>
              <CardDescription>
                So we can reach you about your certification call.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ConfirmEmailStep onConfirmed={() => setStep(1)} />
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Name your practice</CardTitle>
              <CardDescription>
                Your advisory organisation: the one your colleagues join
                and the name on your clients' engagements.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firm-name">Organisation name</Label>
                <Input
                  id="firm-name"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  placeholder="Lindqvist Automation"
                  data-testid="advisor-firm-name"
                />
              </div>
              <Button
                onClick={createPractice}
                disabled={busy || !firmName.trim()}
                data-testid="advisor-firm-submit"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Write your profile</CardTitle>
              <CardDescription>
                What a company reads before deciding to talk to you. You can
                change all of it later, and nothing is public until you publish
                it and are certified.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="display-name">Your name, as clients should see it</Label>
                <Input
                  id="display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Ada Lindqvist"
                  data-testid="advisor-display-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="headline">Headline</Label>
                <Input
                  id="headline"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Automating food distribution back-offices"
                  data-testid="advisor-headline"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio">What you do, and who for</Label>
                <Textarea
                  id="bio"
                  rows={5}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="The businesses you know, the work you have done, and where you start."
                  data-testid="advisor-bio"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <CountrySelect value={country} onChange={setCountry} testId="advisor-country" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="languages">Languages</Label>
                  <Input
                    id="languages"
                    value={languages}
                    onChange={(e) => setLanguages(e.target.value)}
                    placeholder="Czech, English"
                    data-testid="advisor-languages"
                  />
                </div>
              </div>
              <Button
                onClick={saveProfile}
                disabled={busy || !displayName.trim()}
                data-testid="advisor-profile-submit"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card data-testid="advisor-onboarding-done">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BadgeCheck className="h-5 w-5 text-amber-500" />
                You are in
              </CardTitle>
              <CardDescription>
                Your advisor area is open. Add the clients you already have,
                map how they work, and start specifying automations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm">
                One thing to know: your profile is not in the directory yet.
                Publish it and its link works for anyone you send it to.
                Getting into the directory, where people who do not know you
                can find you, takes a short course and a call with someone at
                BitSwan. Do that when you are ready; there is no hurry, and
                nothing else is held back until then.
              </p>
              <Button onClick={() => navigate('/advisor')} data-testid="advisor-onboarding-finish">
                Open my advisor area
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}
